import { useTranslation } from 'react-i18next';
import { FilterInputProps } from '@customer/filters/types';
import { useAppSelector } from '@customer/store/hooks';
import { setFaults } from '@customer/store/slices/filtersSlice';
import { useMemo } from 'react';
import { SelfTestOption, selfTests } from '@customer/config';
import { getSelfTestName } from '@customer/shared/utils/translationHelpers';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import { MultiselectFilter } from './MultiselectFilter';

export const getFaultsInFaultCategories = (categoryIds: number[]): number[] => {
  const selfTestsInCategories: SelfTestOption[] = selfTests.filter((selfTest: SelfTestOption) =>
    categoryIds.includes(selfTest[2]),
  );
  return selfTestsInCategories.map((selfTest: SelfTestOption) => selfTest[0]);
};

const useFaultOptions = (): DropdownItemProps<number>[] => {
  const { t } = useTranslation();

  const faultCategories = useAppSelector(state => state.filters.draft.faultCategories);

  return useMemo(() => {
    const selfTestIds: number[] = getFaultsInFaultCategories(faultCategories);

    const tuples = selfTestIds
      .map(id => ({ id, label: getSelfTestName(t, id) }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return tuples.map(({ id, label }) => {
      const stringValue = id.toString();
      return { key: stringValue, value: id, stringValue, label };
    });
  }, [t, faultCategories]);
};

export const FaultsFilter = ({
  label,
  elementRef,
}: FilterInputProps<HTMLVdsBasicDropdownMultiselectElement>) => {
  const { t } = useTranslation();

  // The key for the Faults dropdown must be changed whenever the faultCategories selection changes.
  const faultCategories = useAppSelector(state => state.filters.draft.faultCategories);
  const faultsRenderKey = `faults--${faultCategories.join('-')}`;

  return (
    <MultiselectFilter<number>
      key={faultsRenderKey}
      filterName="faults"
      label={label ?? t('TestName')}
      action={setFaults}
      elementRef={elementRef}
      disabledOnEmpty={true}
      useItemsHook={useFaultOptions}
    />
  );
};

export default FaultsFilter;
